import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  Calendar, Clock, MapPin, User, Ticket, Tag, ChevronRight, ChevronDown,
  ArrowRight, ExternalLink, Phone, Mail, Globe, Facebook, Instagram, Send,
  Share2, Copy, Check, FileText, FileImage, Archive, FileCode,
  Music, Utensils, ShoppingBag, Home as HomeIcon, MapPin as MapPinIcon,
  Wifi, Baby, Dog, Users as UsersIcon, Car, Accessibility, Coffee, Toilet,
  Youtube, AlertTriangle, Sparkles,
} from "lucide-react";
import { publicApi } from "../lib/api";
import { SITE_DOMAIN } from "../config/site";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const MONTHS_SHORT = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const WEEKDAYS = ["Domenica","Lunedì","Martedì","Mercoledì","Giovedì","Venerdì","Sabato"];

// Icon dictionary — pure look-up, add more here without touching UI.
const AMENITY_ICONS = {
  parking: Car, accessible: Accessibility, kids_area: Baby, pets_allowed: Dog,
  food_point: Coffee, restrooms: Toilet, wifi: Wifi, shelter: HomeIcon,
  first_aid: AlertTriangle, info_point: Sparkles, coat_check: ShoppingBag,
  stroller_friendly: Baby,
};
const AMENITY_LABELS = {
  parking: "Parcheggio", accessible: "Accessibile", kids_area: "Area bambini",
  pets_allowed: "Animali ammessi", food_point: "Punto ristoro", restrooms: "Servizi igienici",
  wifi: "Wi-Fi", shelter: "Riparato pioggia", first_aid: "Primo soccorso",
  info_point: "Info point", coat_check: "Guardaroba", stroller_friendly: "Passeggini",
};
const BADGE_LABELS = {
  free: "Gratuito", music: "Musica dal vivo", family: "Per famiglie",
  pet_friendly: "Pet friendly", accessible: "Accessibile", street_food: "Street food",
  culture: "Cultura", booking_required: "Su prenotazione", outdoor: "All'aperto",
  indoor: "Al chiuso", livestream: "Anche in streaming",
};

const cx = (...a) => a.filter(Boolean).join(" ");

const parseDate = (iso) => (iso ? new Date(iso) : null);

const formatDate = (iso, { short = false } = {}) => {
  const d = parseDate(iso); if (!d) return "";
  const wd = WEEKDAYS[d.getDay()];
  const day = d.getDate();
  const mo = short ? MONTHS_SHORT[d.getMonth()] : MONTHS[d.getMonth()];
  return `${wd}, ${day} ${mo} ${d.getFullYear()}`;
};

const formatTime = (iso) => {
  const d = parseDate(iso); if (!d) return "";
  return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

const fileIcon = (mime) => {
  if (!mime) return FileText;
  if (mime.startsWith("image/")) return FileImage;
  if (mime.includes("zip") || mime.includes("compressed")) return Archive;
  if (mime.includes("json") || mime.includes("xml") || mime.includes("csv")) return FileCode;
  return FileText;
};

// Build .ics file content for "Add to calendar" (Apple/Outlook).
const buildIcs = (event) => {
  const dtStamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const dtStart = new Date(event.start_at).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const dtEnd = event.end_at
    ? new Date(event.end_at).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"
    : dtStart;
  const escape = (s) => (s || "").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//CCN Sanluri//Events//IT",
    "BEGIN:VEVENT",
    `UID:${event.id}@${SITE_DOMAIN.HOST}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${escape(event.title)}`,
    `DESCRIPTION:${escape(event.short_description || event.description || "")}`,
    `LOCATION:${escape([event.location_name, event.address].filter(Boolean).join(", "))}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
};

const googleCalendarUrl = (event) => {
  const fmt = (d) => new Date(d).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${fmt(event.start_at)}/${event.end_at ? fmt(event.end_at) : fmt(event.start_at)}`,
    details: event.short_description || "",
    location: [event.location_name, event.address].filter(Boolean).join(", "),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const mapsUrl = (event) => {
  if (event.latitude && event.longitude)
    return `https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`;
  const q = encodeURIComponent([event.location_name, event.address].filter(Boolean).join(" "));
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
};

const mapsDirectionsUrl = (event) => {
  if (event.latitude && event.longitude)
    return `https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}`;
  const q = encodeURIComponent([event.location_name, event.address].filter(Boolean).join(" "));
  return `https://www.google.com/maps/dir/?api=1&destination=${q}`;
};

// ---------------------------------------------------------------------------
// Sections — each renders NULL when no data. Every section is a pure component.
// ---------------------------------------------------------------------------

const Breadcrumb = ({ title }) => (
  <nav aria-label="Breadcrumb" className="pt-28 pb-2 text-[13px] text-slate-500 flex items-center gap-2 flex-wrap">
    <Link to="/" className="hover:text-[#12964A] transition-colors">Home</Link>
    <ChevronRight className="w-3 h-3 text-slate-300" />
    <Link to="/eventi" className="hover:text-[#12964A] transition-colors">Eventi</Link>
    <ChevronRight className="w-3 h-3 text-slate-300" />
    <span className="text-slate-900 font-medium truncate max-w-[60vw]">{title}</span>
  </nav>
);

const HeroSection = ({ event, onAddCalendar, onOpenMaps }) => {
  const d = parseDate(event.start_at);
  return (
    <section className="relative rounded-[28px] md:rounded-[32px] overflow-hidden shadow-[0_20px_44px_rgba(15,23,42,0.10)] bg-stone-900 min-h-[440px] md:min-h-[560px] flex items-end">
      {event.cover_image && (
        <img
          src={event.cover_image}
          alt=""
          loading="eager"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: `${event.cover_focal_x ?? 50}% ${event.cover_focal_y ?? 50}%` }}
        />
      )}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(11,15,12,.05) 0%, rgba(11,15,12,.15) 40%, rgba(11,15,12,.85) 100%), linear-gradient(90deg, rgba(11,15,12,.55) 0%, rgba(11,15,12,.25) 40%, rgba(11,15,12,0) 70%)",
        }}
      />
      <div className="relative z-10 text-white p-8 md:p-14 max-w-4xl">
        <div className="inline-flex items-center gap-3 bg-white/10 border border-white/25 backdrop-blur px-3 py-2 rounded-full text-xs">
          {event.category_label && (
            <span className="bg-[#12964A] text-white px-2.5 py-1 rounded-full uppercase tracking-[0.12em] text-[11px] font-semibold">
              {event.category_label}
            </span>
          )}
          {event.city && <span className="text-white/90">{event.city} · Sardegna</span>}
        </div>
        {d && (
          <div className="flex items-center gap-5 mt-6">
            <div className="bg-white/95 text-stone-900 rounded-2xl px-3.5 py-2.5 min-w-[76px] text-center leading-none">
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#12964A] font-semibold">{MONTHS_SHORT[d.getMonth()]}</div>
              <div className="text-4xl font-display font-bold my-1">{d.getDate()}</div>
              <div className="text-xs text-slate-500">{d.getFullYear()}</div>
            </div>
            <div>
              <div className="font-semibold text-[15px]">{formatDate(event.start_at)}</div>
              <div className="text-white/75 text-[13px]">
                {!event.all_day && `Dalle ${formatTime(event.start_at)}${event.end_at ? ` alle ${formatTime(event.end_at)}` : ""}`}
                {event.is_free && <span className="ml-2">· Ingresso libero</span>}
              </div>
            </div>
          </div>
        )}
        <h1 className="font-display font-bold text-4xl md:text-6xl lg:text-[64px] leading-[1.05] tracking-tight mt-8">
          {event.title}
        </h1>
        {event.slogan && (
          <p className="mt-3 text-white/80 text-lg italic">{event.slogan}</p>
        )}
        {event.short_description && (
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl leading-relaxed">
            {event.short_description}
          </p>
        )}
        <div className="flex flex-wrap gap-3 mt-8">
          <button
            onClick={onAddCalendar}
            data-testid="hero-cta-calendar"
            className="inline-flex items-center gap-2 bg-[#F4E500] hover:bg-[#F7EA1A] text-[#1B1900] px-6 py-3.5 rounded-full text-sm font-semibold shadow-[0_6px_16px_rgba(244,229,0,.35)] hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(244,229,0,.45)] transition-all duration-300"
          >
            <Calendar className="w-4 h-4" /> Aggiungi al calendario
          </button>
          {(event.address || event.latitude) && (
            <a
              href={mapsUrl(event)}
              target="_blank" rel="noopener noreferrer"
              onClick={onOpenMaps}
              className="inline-flex items-center gap-2 bg-white/12 border border-white/28 text-white backdrop-blur px-6 py-3.5 rounded-full text-sm hover:bg-white/22 hover:-translate-y-0.5 transition-all duration-300"
            >
              <MapPin className="w-4 h-4" /> Apri su Google Maps
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

const BadgesRow = ({ badges = [], isFree }) => {
  const all = [...(isFree ? ["free"] : []), ...(badges || []).filter((b) => b !== "free")];
  if (!all.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-8">
      {all.map((b) => (
        <span
          key={b}
          className={cx(
            "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm border",
            b === "free"
              ? "bg-[#E8F5EC] text-[#0E7C3D] border-transparent font-semibold"
              : "bg-white text-stone-800 border-stone-200"
          )}
        >
          <Check className="w-3.5 h-3.5 text-[#12964A]" />
          {BADGE_LABELS[b] || b}
        </span>
      ))}
    </div>
  );
};

const PosterPublicSection = ({ event }) => {
  const [open, setOpen] = React.useState(false);
  if (!event?.poster_url || !event?.poster_is_public) return null;
  const isPdf = (event.poster_mime_type === "application/pdf") || /\.pdf(\?|$)/i.test(event.poster_url);
  const thumb = event.poster_thumbnail_url || (!isPdf ? event.poster_url : null);
  const downloadHref = `/api/events/${event.id}/poster/download`;
  return (
    <section className="mb-14" data-testid="public-poster-section">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#12964A] font-semibold mb-2">
        Locandina dell&apos;evento
      </div>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-stone-900 mb-4 tracking-tight">
        Guarda o scarica la locandina ufficiale
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-[240px_1fr] gap-6 items-start">
        <div>
          {isPdf ? (
            <a
              href={event.poster_url}
              target="_blank"
              rel="noreferrer"
              className="block relative group"
              data-testid="public-poster-thumb-pdf"
            >
              {thumb ? (
                <img
                  src={thumb}
                  alt="Anteprima locandina"
                  className="w-full aspect-[3/4] object-cover rounded-xl shadow-md group-hover:shadow-lg transition"
                />
              ) : (
                <div className="w-full aspect-[3/4] rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200">
                  <FileText className="w-12 h-12" />
                </div>
              )}
              <span className="absolute bottom-2 left-2 rounded-md bg-rose-600 text-white text-xs font-bold uppercase tracking-widest px-2 py-1 shadow">
                PDF
              </span>
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="w-full block relative group"
              data-testid="public-poster-thumb"
            >
              <img
                src={thumb || event.poster_url}
                alt="Anteprima locandina"
                className="w-full aspect-[3/4] object-cover rounded-xl shadow-md group-hover:shadow-lg transition"
              />
              <span className="absolute inset-0 rounded-xl bg-slate-900/0 group-hover:bg-slate-900/25 transition flex items-center justify-center text-white opacity-0 group-hover:opacity-100 text-sm font-medium">
                Apri a schermo intero
              </span>
            </button>
          )}
        </div>
        <div className="text-slate-700 text-[17px] leading-[1.75]">
          <p>
            Puoi visualizzare la locandina ufficiale dell&apos;evento{" "}
            {isPdf ? "in formato PDF" : "a schermo intero"} oppure scaricarla per
            stamparla o condividerla.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            {isPdf ? (
              <a
                href={event.poster_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#12964A] text-white font-medium hover:bg-[#0E7C3D] transition"
                data-testid="public-poster-open-pdf"
              >
                Apri PDF <ExternalLink className="w-4 h-4" />
              </a>
            ) : (
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#12964A] text-white font-medium hover:bg-[#0E7C3D] transition"
                data-testid="public-poster-open-fullscreen"
              >
                Apri a schermo intero
              </button>
            )}
            <a
              href={downloadHref}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-300 bg-white text-slate-800 font-medium hover:bg-slate-50 transition"
              data-testid="public-poster-download"
            >
              Scarica locandina
            </a>
          </div>
        </div>
      </div>

      {/* Fullscreen viewer (images only) */}
      {open && !isPdf && (
        <div
          className="fixed inset-0 z-[80] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          data-testid="public-poster-fullscreen"
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 text-white p-2"
            aria-label="Chiudi anteprima"
          >
            ✕
          </button>
          <img
            src={event.poster_url}
            alt="Locandina"
            className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

const DescriptionSection = ({ event }) => {
  if (!event.description && !event.short_description) return null;
  return (
    <section className="mb-14">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#12964A] font-semibold mb-2">Descrizione</div>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-stone-900 mb-4 tracking-tight">
        La festa che racconta {event.city || "Sanluri"}
      </h2>
      {event.description ? (
        <div className="prose max-w-none text-[17px] leading-[1.75] text-slate-700 whitespace-pre-wrap">
          {event.description}
        </div>
      ) : (
        <p className="text-[17px] leading-[1.75] text-slate-700">{event.short_description}</p>
      )}
    </section>
  );
};

const TimelineSection = ({ timeline }) => {
  if (!timeline || !timeline.length) return null;
  return (
    <section className="mb-14">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#12964A] font-semibold mb-2">Programma</div>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-stone-900 mb-6 tracking-tight">
        Il programma
      </h2>
      <div className="relative pl-7">
        <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#12964A] to-[#E8F5EC] rounded-full" />
        {timeline.map((t, i) => (
          <div key={i} className="relative pb-6 last:pb-0">
            <div className="absolute -left-[22px] top-5 w-3.5 h-3.5 rounded-full bg-white border-[3px] border-[#12964A] shadow-[0_0_0_4px_#F0FAF3]" />
            <div className="pl-2 pt-3">
              <div className="text-[15px] font-display font-bold text-[#0E7C3D]">{t.time || ""}</div>
              {t.title && <div className="font-semibold text-stone-900 mt-0.5">{t.title}</div>}
              {t.description && <div className="text-slate-500 text-sm mt-1">{t.description}</div>}
              {t.location && <div className="text-slate-400 text-xs mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.location}</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const GallerySection = ({ gallery }) => {
  const items = (gallery || []).filter((g) => g && (g.url || g.thumbnail_url));
  const [active, setActive] = useState(0);
  if (!items.length) return null;
  const cur = items[active] || items[0];
  return (
    <section className="mb-14">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#12964A] font-semibold mb-2">Gallery</div>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-stone-900 mb-6 tracking-tight">Foto e video</h2>
      <div className="rounded-3xl overflow-hidden bg-slate-100 aspect-[16/9]">
        {cur.type === "youtube" && (
          <iframe title="video" className="w-full h-full" src={`https://www.youtube.com/embed/${cur.url.split(/[/=]/).pop()}`} allowFullScreen />
        )}
        {cur.type === "vimeo" && (
          <iframe title="video" className="w-full h-full" src={`https://player.vimeo.com/video/${cur.url.split("/").pop()}`} allowFullScreen />
        )}
        {cur.type === "video" && (
          <video controls className="w-full h-full object-cover" src={cur.url} />
        )}
        {(!cur.type || cur.type === "image") && (
          <img src={cur.url || cur.thumbnail_url} alt={cur.alt || ""} loading="lazy" className="w-full h-full object-cover" />
        )}
      </div>
      {items.length > 1 && (
        <div className="grid grid-cols-5 gap-2.5 mt-3">
          {items.map((g, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={cx(
                "aspect-square rounded-xl overflow-hidden transition-all duration-300",
                i === active ? "opacity-100 ring-2 ring-[#12964A] ring-offset-2" : "opacity-70 hover:opacity-100"
              )}
            >
              <img src={g.thumbnail_url || g.url} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

const MapSection = ({ event }) => {
  if (!event.address && !event.latitude) return null;
  const lat = event.latitude, lng = event.longitude;
  const embed = lat && lng
    ? `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lng}&zoom=${event.map_zoom || 15}&size=1200x340&markers=${lat},${lng},red`
    : null;
  return (
    <section className="mb-14">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#12964A] font-semibold mb-2">Dove</div>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-stone-900 mb-1 tracking-tight">
        {event.location_name || event.city || "Sanluri"}
      </h2>
      {event.address && <p className="text-slate-500 mb-6">{event.address}</p>}
      <div className="relative rounded-3xl overflow-hidden h-[340px] bg-[#E8F5EC]">
        {embed && <img src={embed} alt="Mappa" loading="lazy" className="w-full h-full object-cover" />}
        <div className="absolute bottom-4 left-4 right-4 flex gap-2">
          <a
            href={mapsDirectionsUrl(event)}
            target="_blank" rel="noopener noreferrer"
            className="flex-1 bg-[#12964A] hover:bg-[#0E7C3D] text-white text-sm font-semibold py-3 rounded-full inline-flex items-center justify-center gap-2 transition-colors"
          >
            <MapPin className="w-4 h-4" /> Calcola percorso
          </a>
          <a
            href={mapsUrl(event)}
            target="_blank" rel="noopener noreferrer"
            className="flex-1 bg-white shadow-sm text-stone-900 text-sm font-semibold py-3 rounded-full inline-flex items-center justify-center gap-2 border border-stone-200 hover:border-[#12964A] transition-colors"
          >
            <ExternalLink className="w-4 h-4" /> Apri in Google Maps
          </a>
        </div>
      </div>
      {event.venue_notes && <p className="mt-3 text-sm text-slate-500 italic">{event.venue_notes}</p>}
    </section>
  );
};

const FaqSection = ({ faqs }) => {
  const [open, setOpen] = useState(null);
  if (!faqs || !faqs.length) return null;
  return (
    <section className="mb-14">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#12964A] font-semibold mb-2">FAQ</div>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-stone-900 mb-6 tracking-tight">Domande frequenti</h2>
      <div className="border-t border-slate-200">
        {faqs.map((f, i) => (
          <div key={i} className="border-b border-slate-200">
            <button
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-center justify-between text-left py-4 text-[16px] font-semibold text-stone-900 hover:text-[#12964A] transition-colors"
            >
              {f.question}
              <ChevronDown className={cx("w-4 h-4 text-slate-400 transition-transform", open === i && "rotate-180")} />
            </button>
            {open === i && (
              <div className="pb-5 text-slate-600 leading-relaxed">{f.answer}</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const DownloadsSection = ({ downloads }) => {
  if (!downloads || !downloads.length) return null;
  return (
    <section className="mb-14">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#12964A] font-semibold mb-2">Download</div>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-stone-900 mb-6 tracking-tight">Materiali dell&apos;evento</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {downloads.map((d, i) => {
          const Icon = fileIcon(d.mime_type);
          return (
            <a
              key={i}
              href={d.url}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-stone-200 hover:border-[#12964A] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-xl bg-[#F0FAF3] text-[#12964A] grid place-items-center flex-shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-stone-900 truncate">{d.title || "Allegato"}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {(d.mime_type || "file").toUpperCase().split("/").pop()} {d.file_size_label && `· ${d.file_size_label}`}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
};

const TagsSection = ({ tags }) => {
  if (!tags || !tags.length) return null;
  return (
    <section className="mb-14">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#12964A] font-semibold mb-3">Tag</div>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t, i) => (
          <span key={i} className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-1">
            {t.startsWith("#") ? t : `#${t}`}
          </span>
        ))}
      </div>
    </section>
  );
};

// ---------- Sidebar cards ----------

const DetailsCard = ({ event, onAddCalendar, onOpenMaps }) => {
  const d = parseDate(event.start_at);
  const rows = [];
  if (d) rows.push({ icon: Calendar, label: "Data", value: formatDate(event.start_at) });
  if (!event.all_day && event.start_at)
    rows.push({ icon: Clock, label: "Orario", value: `${formatTime(event.start_at)}${event.end_at ? ` – ${formatTime(event.end_at)}` : ""}` });
  if (event.location_name || event.address)
    rows.push({ icon: MapPin, label: "Luogo", value: [event.location_name, event.address].filter(Boolean).join(" · ") });
  if (event.category_label) rows.push({ icon: Tag, label: "Categoria", value: event.category_label });
  if (event.capacity) rows.push({ icon: UsersIcon, label: "Capienza", value: `${event.capacity} posti` });
  if (event.is_free) rows.push({ icon: Ticket, label: "Costo", value: "Ingresso libero", accent: true });
  else if (event.price_from) rows.push({ icon: Ticket, label: "Costo", value: `Da ${event.price_from} ${event.price_currency || "EUR"}${event.price_note ? ` · ${event.price_note}` : ""}` });

  return (
    <div className="bg-white rounded-3xl p-6 border border-stone-200 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
      <h3 className="font-display font-bold text-[12px] uppercase tracking-[0.22em] text-slate-500 mb-4">Dettagli evento</h3>
      <ul>
        {rows.map((r, i) => (
          <li key={i} className={cx("flex items-start gap-3.5 py-3", i > 0 && "border-t border-slate-100")}>
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#F0FAF3] text-[#12964A] grid place-items-center">
              <r.icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{r.label}</div>
              <div className={cx("text-[15px] font-medium mt-0.5", r.accent ? "text-[#0E7C3D]" : "text-stone-900")}>{r.value}</div>
            </div>
          </li>
        ))}
      </ul>
      <div className="flex flex-col gap-2 mt-5">
        <button
          onClick={onAddCalendar}
          data-testid="side-cta-calendar"
          className="bg-stone-950 hover:bg-[#12964A] text-white text-sm font-semibold py-3 rounded-2xl inline-flex items-center justify-center gap-2 transition-colors hover:-translate-y-0.5 duration-300"
        >
          <Calendar className="w-4 h-4" /> Aggiungi al calendario
        </button>
        {(event.address || event.latitude) && (
          <a
            href={mapsUrl(event)}
            target="_blank" rel="noopener noreferrer"
            onClick={onOpenMaps}
            className="bg-white border border-stone-200 hover:border-[#12964A] hover:text-[#12964A] text-stone-800 text-sm font-semibold py-3 rounded-2xl inline-flex items-center justify-center gap-2 transition-colors"
          >
            <MapPin className="w-4 h-4" /> Apri su Google Maps
          </a>
        )}
        {event.ticket_url && (
          <a
            href={event.ticket_url}
            target="_blank" rel="noopener noreferrer"
            className="bg-[#F4E500] hover:bg-[#F7EA1A] text-[#1B1900] text-sm font-semibold py-3 rounded-2xl inline-flex items-center justify-center gap-2 transition-colors hover:-translate-y-0.5 duration-300"
          >
            <Ticket className="w-4 h-4" /> Acquista biglietti
          </a>
        )}
      </div>
    </div>
  );
};

const OrganizersCard = ({ organizers }) => {
  if (!organizers || !organizers.length) return null;
  return (
    <div className="bg-white rounded-3xl p-6 border border-stone-200">
      <h3 className="font-display font-bold text-[12px] uppercase tracking-[0.22em] text-slate-500 mb-4">
        {organizers.length > 1 ? "Organizzatori" : "Organizzatore"}
      </h3>
      {organizers.map((o, i) => (
        <div key={o.id || i} className={cx(i > 0 && "border-t border-slate-100 pt-4 mt-4")}>
          <div className="font-semibold text-stone-900">{o.name}</div>
          {o.note && <div className="text-sm text-slate-500 mt-1">{o.note}</div>}
          <div className="flex flex-col gap-2 mt-3 text-sm">
            {o.phone && <a href={`tel:${o.phone}`} className="inline-flex items-center gap-2 text-slate-700 hover:text-[#12964A]"><Phone className="w-4 h-4 text-[#12964A]" /> {o.phone}</a>}
            {o.email && <a href={`mailto:${o.email}`} className="inline-flex items-center gap-2 text-slate-700 hover:text-[#12964A]"><Mail className="w-4 h-4 text-[#12964A]" /> {o.email}</a>}
            {o.website && <a href={o.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-slate-700 hover:text-[#12964A]"><Globe className="w-4 h-4 text-[#12964A]" /> {o.website.replace(/^https?:\/\//, "")}</a>}
          </div>
          {(o.facebook || o.instagram || o.telegram) && (
            <div className="flex gap-2 mt-3">
              {o.facebook && <a href={o.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-white border border-slate-200 hover:border-[#1877F2] hover:text-[#1877F2] grid place-items-center transition-colors"><Facebook className="w-4 h-4" /></a>}
              {o.instagram && <a href={o.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-white border border-slate-200 hover:border-[#E1306C] hover:text-[#E1306C] grid place-items-center transition-colors"><Instagram className="w-4 h-4" /></a>}
              {o.telegram && <a href={o.telegram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-white border border-slate-200 hover:border-[#29A9EB] hover:text-[#29A9EB] grid place-items-center transition-colors"><Send className="w-4 h-4" /></a>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const AmenitiesCard = ({ amenities }) => {
  if (!amenities || !amenities.length) return null;
  return (
    <div className="bg-white rounded-3xl p-6 border border-stone-200">
      <h3 className="font-display font-bold text-[12px] uppercase tracking-[0.22em] text-slate-500 mb-4">Informazioni utili</h3>
      <div className="grid grid-cols-2 gap-2">
        {amenities.map((a) => {
          const Icon = AMENITY_ICONS[a] || Check;
          return (
            <div key={a} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-[#F0FAF3] text-[#0E7C3D] text-[13px] font-medium">
              <Icon className="w-4 h-4 text-[#12964A]" />
              {AMENITY_LABELS[a] || a}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ShareCard = ({ event, onShare }) => {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareUrl = encodeURIComponent(url);
  const shareText = encodeURIComponent(event.title);
  const copyLink = () => {
    navigator.clipboard?.writeText(url).then(() => { setCopied(true); onShare?.(); setTimeout(() => setCopied(false), 2000); });
  };
  return (
    <div className="bg-white rounded-3xl p-6 border border-stone-200">
      <h3 className="font-display font-bold text-[12px] uppercase tracking-[0.22em] text-slate-500 mb-4">Condividi evento</h3>
      <div className="flex gap-2">
        <a onClick={onShare} href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="flex-1 h-11 rounded-xl border border-slate-200 grid place-items-center text-stone-700 hover:border-[#1877F2] hover:text-[#1877F2] transition-colors" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
        <a onClick={onShare} href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noopener noreferrer" className="flex-1 h-11 rounded-xl border border-slate-200 grid place-items-center text-stone-700 hover:border-[#25D366] hover:text-[#25D366] transition-colors" aria-label="WhatsApp">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12.05 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.6 5.94L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.44h.01c6.55 0 11.89-5.33 11.89-11.89 0-3.18-1.24-6.16-3.48-8.4A11.82 11.82 0 0 0 12.05 0z"/></svg>
        </a>
        <a onClick={onShare} href={`https://t.me/share/url?url=${shareUrl}&text=${shareText}`} target="_blank" rel="noopener noreferrer" className="flex-1 h-11 rounded-xl border border-slate-200 grid place-items-center text-stone-700 hover:border-[#29A9EB] hover:text-[#29A9EB] transition-colors" aria-label="Telegram"><Send className="w-4 h-4" /></a>
        <button onClick={copyLink} className="flex-1 h-11 rounded-xl border border-slate-200 grid place-items-center text-stone-700 hover:border-[#12964A] hover:text-[#12964A] transition-colors" aria-label="Copia link">
          {copied ? <Check className="w-4 h-4 text-[#12964A]" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const EventLinksCard = ({ event }) => {
  const links = [
    event.event_website && { icon: Globe, label: "Sito ufficiale", url: event.event_website },
    event.event_facebook && { icon: Facebook, label: "Facebook", url: event.event_facebook },
    event.event_instagram && { icon: Instagram, label: "Instagram", url: event.event_instagram },
    event.event_youtube && { icon: Youtube, label: "YouTube", url: event.event_youtube },
  ].filter(Boolean);
  if (!links.length) return null;
  return (
    <div className="bg-white rounded-3xl p-6 border border-stone-200">
      <h3 className="font-display font-bold text-[12px] uppercase tracking-[0.22em] text-slate-500 mb-4">Link ufficiali</h3>
      <div className="flex flex-col gap-2">
        {links.map((l) => (
          <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 py-2 text-slate-700 hover:text-[#12964A] text-sm">
            <l.icon className="w-4 h-4 text-[#12964A]" /> {l.label} <ExternalLink className="w-3 h-3 text-slate-400 ml-auto" />
          </a>
        ))}
      </div>
    </div>
  );
};

// ---------- Cross-navigation ----------

const NearbyBusinesses = ({ items }) => {
  if (!items || !items.length) return null;
  return (
    <section className="mt-8">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#12964A] font-semibold mb-2">Attività nelle vicinanze</div>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-stone-900 mb-2 tracking-tight">Prima o dopo l&apos;evento, scopri…</h2>
      <p className="text-slate-500 mb-6">Le attività del CCN a pochi passi dall&apos;evento.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((b) => (
          <Link key={b.id} to={`/attivita/${b.slug}`} className="group bg-white border border-stone-200 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_44px_rgba(15,23,42,0.10)] hover:border-[#E8F5EC] transition-all duration-300">
            <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
              {b.cover_image && <img src={b.cover_image} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
            </div>
            <div className="p-4">
              {b.category?.name && <div className="text-[10px] uppercase tracking-[0.18em] text-[#12964A] font-semibold">{b.category.name}</div>}
              <div className="font-display font-bold text-[16px] text-stone-900 mt-0.5">{b.name}</div>
              <div className="text-xs text-slate-500 mt-1.5 inline-flex items-center gap-1"><MapPinIcon className="w-3 h-3" /> a {b.distance_label} · {b.walk_minutes} min a piedi</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const RelatedEvents = ({ items }) => {
  if (!items || !items.length) return null;
  return (
    <section className="mt-14">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#12964A] font-semibold mb-2">Potrebbero interessarti</div>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-stone-900 mb-2 tracking-tight">Altri eventi a Sanluri</h2>
      <p className="text-slate-500 mb-6">Segna in agenda i prossimi appuntamenti del CCN.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((e) => (
          <Link key={e.id} to={`/eventi/${e.slug}`} className="flex gap-3.5 p-3 bg-white border border-stone-200 rounded-2xl hover:-translate-y-1 hover:shadow-[0_20px_44px_rgba(15,23,42,0.08)] hover:border-[#E8F5EC] transition-all duration-300 group">
            <div className="w-32 aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
              {e.cover_image && <img src={e.cover_image} alt="" loading="lazy" className="w-full h-full object-cover" />}
            </div>
            <div className="flex flex-col justify-center min-w-0">
              <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(e.start_at, { short: true })}</div>
              <div className="font-display font-semibold text-stone-900 text-[16px] mt-1 group-hover:text-[#12964A] transition-colors line-clamp-2">{e.title}</div>
              {e.short_description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{e.short_description}</div>}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const DiscoverSanluri = () => (
  <section className="mt-20 -mx-6 lg:-mx-10 px-6 lg:px-10 py-14 border-y border-slate-200 bg-gradient-to-b from-[#E8F5EC]/40 to-[#F0FAF3]/60">
    <div className="max-w-[1200px] mx-auto text-center mb-8">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#12964A] font-semibold">Scopri anche</div>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-stone-900 mt-2 tracking-tight">Continua l&apos;esperienza a Sanluri</h2>
      <p className="text-slate-500 mt-2">Naviga il portale e trova tutto quello che serve.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-[1200px] mx-auto">
      {[
        { icon: ShoppingBag, title: "Attività associate", desc: "Oltre 200 attività del CCN Sanluri.", to: "/attivita", cta: "Esplora" },
        { icon: MapPinIcon, title: "Luoghi turistici", desc: "Castello, musei, siti storici del Campidano.", to: "/categoria/turismo", cta: "Scopri" },
        { icon: Utensils, title: "Dove mangiare", desc: "Ristoranti, pizzerie e trattorie tipiche.", to: "/categoria/ristorazione", cta: "Prenota" },
        { icon: HomeIcon, title: "Dove dormire", desc: "B&B, hotel e agriturismi selezionati.", to: "/categoria/turismo", cta: "Scopri" },
      ].map((c) => (
        <Link key={c.title} to={c.to} className="group bg-white border border-stone-200 rounded-3xl p-6 flex flex-col gap-4 hover:-translate-y-1 hover:shadow-[0_20px_44px_rgba(15,23,42,0.10)] hover:border-[#E8F5EC] transition-all duration-300">
          <div className="w-13 h-13 w-[52px] h-[52px] rounded-2xl bg-[#F0FAF3] text-[#12964A] grid place-items-center group-hover:scale-105 transition-transform duration-300"><c.icon className="w-5 h-5" /></div>
          <div>
            <div className="font-display font-bold text-[18px] text-stone-900">{c.title}</div>
            <div className="text-sm text-slate-500 mt-1">{c.desc}</div>
          </div>
          <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#12964A]">{c.cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" /></div>
        </Link>
      ))}
    </div>
  </section>
);

// ---------- Calendar dropdown ----------

const CalendarDropdown = ({ open, event, onClose }) => {
  if (!open) return null;
  const downloadIcs = () => {
    const blob = new Blob([buildIcs(event)], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${event.slug || "evento"}.ics`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    onClose?.();
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-[0_30px_60px_-20px_rgba(15,23,42,0.30)]" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-lg text-stone-900 mb-4">Aggiungi al calendario</h3>
        <div className="flex flex-col gap-2">
          <a href={googleCalendarUrl(event)} target="_blank" rel="noopener noreferrer" onClick={onClose} className="flex items-center gap-3 p-3 rounded-2xl border border-slate-200 hover:border-[#12964A] hover:bg-[#F0FAF3] transition-colors">
            <Calendar className="w-5 h-5 text-[#12964A]" />
            <div><div className="font-semibold text-stone-900">Google Calendar</div><div className="text-xs text-slate-500">Apri nel tuo account Google</div></div>
          </a>
          <button onClick={downloadIcs} className="flex items-center gap-3 p-3 rounded-2xl border border-slate-200 hover:border-[#12964A] hover:bg-[#F0FAF3] transition-colors text-left">
            <Calendar className="w-5 h-5 text-[#12964A]" />
            <div><div className="font-semibold text-stone-900">Apple / Outlook</div><div className="text-xs text-slate-500">Scarica file .ics</div></div>
          </button>
        </div>
        <button onClick={onClose} className="mt-4 w-full text-sm text-slate-500 hover:text-slate-800">Annulla</button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const EventDetail = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calOpen, setCalOpen] = useState(false);
  const trackedRef = React.useRef(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    publicApi.getEvent(slug)
      .then((r) => { if (alive) { setEvent(r.data); setError(null); } })
      .catch((e) => { if (alive) { setError(e?.response?.status || "err"); setEvent(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  // Fire a single view track per mount
  useEffect(() => {
    if (event && !trackedRef.current) {
      trackedRef.current = true;
      publicApi.trackEvent(slug, "view").catch(() => {});
    }
  }, [event, slug]);

  useEffect(() => {
    if (!event) return;
    const t = event.meta_title || `${event.title} · Eventi · CCN Sanluri`;
    const d = event.meta_description || event.short_description || "";
    document.title = t;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", d);
  }, [event]);

  const onAddCalendar = () => { setCalOpen(true); publicApi.trackEvent(slug, "calendar").catch(() => {}); };
  const onOpenMaps = () => { publicApi.trackEvent(slug, "maps").catch(() => {}); };
  const onShare = () => { publicApi.trackEvent(slug, "share").catch(() => {}); };

  if (loading) return (
    <div className="pt-40 pb-20 text-center text-slate-500">Caricamento evento…</div>
  );
  if (error === 404 || !event) return <Navigate to="/eventi" replace />;

  return (
    <div className="pt-6 lg:pt-8">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <Breadcrumb title={event.title} />
        <div className="mt-4">
          <HeroSection event={event} onAddCalendar={onAddCalendar} onOpenMaps={onOpenMaps} />
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-10 items-start">
          <main>
            <BadgesRow badges={event.badges} isFree={event.is_free} />
            <DescriptionSection event={event} />
            <PosterPublicSection event={event} />
            <TimelineSection timeline={event.timeline} />
            <GallerySection gallery={event.gallery} />
            <MapSection event={event} />
            <FaqSection faqs={event.faqs} />
            <DownloadsSection downloads={event.downloads} />
            <TagsSection tags={event.tags} />
          </main>

          <aside className="lg:sticky lg:top-24 flex flex-col gap-4">
            <DetailsCard event={event} onAddCalendar={onAddCalendar} onOpenMaps={onOpenMaps} />
            <OrganizersCard organizers={event.organizers} />
            <EventLinksCard event={event} />
            <AmenitiesCard amenities={event.amenities} />
            <ShareCard event={event} onShare={onShare} />
          </aside>
        </div>

        <NearbyBusinesses items={event.nearby_businesses} />
        <RelatedEvents items={event.related_events} />
      </div>

      <DiscoverSanluri />
      <CalendarDropdown open={calOpen} event={event} onClose={() => setCalOpen(false)} />
    </div>
  );
};

export default EventDetail;
